/**
 * CLI 全体で共通する help JSON スキーマ。
 * root / builtin-command / api-command でトップレベル骨格を揃える。
 */

export type HelpKind = 'root' | 'builtin-command' | 'api-command';

export interface HelpPositional {
  name: string;
  type: string;
  required: boolean;
  description: string;
}

export interface HelpOption {
  name: string;
  type: string;
  description: string;
  required?: boolean;
  choices?: string[];
  default?: string | undefined;
}

export interface HelpInputSchema {
  pathParams: Array<{ name: string; type: string; required: boolean; description: string; example?: unknown }>;
  queryParams: Array<{ name: string; type: string; required: boolean; description: string; example?: unknown }>;
  requestBody: {
    properties: Array<{ name: string; type: string; required: boolean; description: string; example?: unknown }>;
  } | null;
  stdinSupported: boolean;
  positionalJsonSupported: boolean;
}

export interface HelpCommandRef {
  name: string;
  kind: 'builtin-command' | 'api-command';
  summary: string;
}

/** root help: acomo --help */
export interface RootHelp {
  command: 'acomo';
  kind: 'root';
  summary: string;
  usage: string;
  commands: HelpCommandRef[];
  globalOptions: HelpOption[];
}

/** builtin / api コマンド help */
export interface CommandHelp {
  command: string;
  kind: 'builtin-command' | 'api-command';
  summary: string;
  usage: string;
  positionals: HelpPositional[];
  options: HelpOption[];
  inputSchema: HelpInputSchema | null;
  minimalArgs: Record<string, unknown> | null;
  exampleCommand: string | null;
}

export type HelpDescriptor = RootHelp | CommandHelp;

export function isRootHelp(h: HelpDescriptor): h is RootHelp {
  return h.kind === 'root';
}

const GLOBAL_OPTIONS: HelpOption[] = [
  {
    name: 'format',
    type: 'string',
    description: '出力形式（API の Accept ヘッダー）。パイプ時は json がデフォルト。',
    choices: ['text', 'json'],
    default: undefined,
  },
  { name: 'help', type: 'boolean', description: 'Show help' },
  { name: 'version', type: 'boolean', description: 'Show version number' },
];

export function buildRootHelp(commands: HelpCommandRef[]): RootHelp {
  return {
    command: 'acomo',
    kind: 'root',
    summary: 'acomo ワークフロー管理プラットフォームの公式 CLI',
    usage: 'acomo <command> [options]',
    commands,
    globalOptions: GLOBAL_OPTIONS,
  };
}

export function buildCommandHelp(params: {
  command: string;
  kind: 'builtin-command' | 'api-command';
  summary: string;
  usage: string;
  positionals: HelpPositional[];
  options: HelpOption[];
  inputSchema: HelpInputSchema | null;
  minimalArgs: Record<string, unknown> | null;
  exampleCommand: string | null;
}): CommandHelp {
  return {
    command: params.command,
    kind: params.kind,
    summary: params.summary,
    usage: params.usage,
    positionals: params.positionals,
    options: params.options,
    inputSchema: params.inputSchema,
    minimalArgs: params.minimalArgs,
    exampleCommand: params.exampleCommand,
  };
}
